// ─────────────────────────────────────────────────────────────
// BudgetScreen.jsx
// Pantalla de presupuesto del usuario para un grupo.
// Muestra el presupuesto total, lo gastado/comprometido,
// y la lista de regalos con precios tal como fueron guardados.
// ─────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { currencySymbol } from "../utils/currencyUtils";

// Si es true, también se quitan los regalos YA COMPRADOS cuando la persona
// los borra de su lista. Si prefieres que los comprados sigan contando
// como "Gastado" aunque los borren, cámbialo a false.
const QUITAR_COMPRADOS_BORRADOS = true;

export default function BudgetScreen({
  lang, darkMode, user, userProfile, setUserProfile,
  currentFamily, userFamilies, groupIcons,
  showBudgetGeneralMode, setShowBudgetGeneralMode,
  showBudgetGroupDropdown, setShowBudgetGroupDropdown,
  editingPriceEntry, setEditingPriceEntry,
  editingPriceValue, setEditingPriceValue,
  budgetInput, setBudgetInput,
  setShowBudgetScreen, setShowBudgetEdit,
  setCurrentFamily,
  getBudgetTotals, getBudgetTotalsAll,
  markAsPurchased, markAsNotPurchased, uncommitGift,
  showToast, tr, exchangeRates,
}) {
  const [savingBudget, setSavingBudget] = useState(false);
  const [showPurchased, setShowPurchased] = useState(false);

  // ── Familia activa ────────────────────────────────────────
  const family = currentFamily;
  const fid = family?.id;
  const sym = currencySymbol(userProfile?.currency || "USD");

  // ── Totales del grupo actual ──────────────────────────────
  const totals = fid
    ? getBudgetTotals(fid, userProfile, exchangeRates)
    : { budget: 0, spent: 0, committed: 0, gifts: [] };

  const usedAmt = totals.spent + totals.committed;
  const pct = totals.budget > 0 ? Math.min(100, (usedAmt / totals.budget) * 100) : 0;
  const barColor = pct > 90 ? "#c0392b" : pct > 70 ? "#e67e22" : "#27ae60";

  // ── Guardar presupuesto ───────────────────────────────────
  const saveBudget = async () => {
    if (!user || !fid) {
      showToast(lang === "es" ? "No se encontró el grupo" : "Group not found");
      return;
    }
    setSavingBudget(true);
    const val = parseFloat(budgetInput) || 0;
    const budgetCurrency = userProfile?.currency || "USD";
    try {
      await updateDoc(doc(db, "users", user.uid), {
        [`groupBudgets.${fid}`]: val,
        [`groupBudgetCurrencies.${fid}`]: budgetCurrency,
      });
      setUserProfile((p) => ({
        ...p,
        groupBudgets: { ...(p?.groupBudgets || {}), [fid]: val },
        groupBudgetCurrencies: { ...(p?.groupBudgetCurrencies || {}), [fid]: budgetCurrency },
      }));
      showToast(tr("budgetSaved") || (lang === "es" ? "Presupuesto guardado" : "Budget saved"));
      setShowBudgetScreen(false);
    } catch (e) {
      console.error("Error al guardar presupuesto:", e);
      showToast(tr("errorSaving") || "Error");
    }
    setSavingBudget(false);
  };

  // ── Limpieza automática: quitar regalos que el dueño borró ──
  // Al abrir la ventana revisa cada wishlist y quita del carrito
  // los regalos que ya no existen en la lista de la otra persona.
  useEffect(() => {
    if (!user) return;
    const list = userProfile?.committedGifts || [];
    if (list.length === 0) return;
    let cancelled = false;

    (async () => {
      const ids = [...new Set(list.map((g) => g.wishlistId).filter(Boolean))];
      const snaps = await Promise.all(
        ids.map((id) => getDoc(doc(db, "wishlists", id)).catch(() => null))
      );

      // wishlistId → ids de regalos que todavía existen
      const existentes = {};
      ids.forEach((id, i) => {
        const snap = snaps[i];
        if (snap === null) return; // error al leer: no tocamos esos regalos
        existentes[id] = snap.exists()
          ? new Set((snap.data().gifts || []).map((x) => x.id))
          : new Set(); // la wishlist completa fue borrada
      });

      const sigueExistiendo = (g) => {
        if (!g.wishlistId || !(g.wishlistId in existentes)) return true;
        if (g.purchased && !QUITAR_COMPRADOS_BORRADOS) return true;
        const gid = g.giftId || g.id || g.gift?.id;
        if (!gid) return true;
        return existentes[g.wishlistId].has(gid);
      };

      const newList = list.filter(sigueExistiendo);
      if (cancelled || newList.length === list.length) return;

      // Seguridad: si fuera a borrar TODO de golpe, probablemente el id
      // del regalo se guarda con otro nombre. Mejor no tocar nada.
      if (newList.length === 0 && list.length > 1) {
        console.warn("Limpieza de carrito cancelada: no coincidió ningún regalo", list[0]);
        return;
      }

      try {
        await updateDoc(doc(db, "users", user.uid), { committedGifts: newList });
        if (!cancelled) setUserProfile((p) => ({ ...p, committedGifts: newList }));
      } catch (e) {
        console.error("Error al limpiar carrito:", e);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  // ── Quitar regalos comprados del historial ───────────────
  // Identifica un regalo por wishlist + id del regalo + grupo
  const giftKey = (g) =>
    (g.wishlistId || "") + "_" + (g.giftId || g.id || g.gift?.id || "") + "_" + (g.familyId || "");

  const saveCommittedGifts = async (newList) => {
    if (!user) return false;
    try {
      await updateDoc(doc(db, "users", user.uid), { committedGifts: newList });
      setUserProfile((p) => ({ ...p, committedGifts: newList }));
      return true;
    } catch (e) {
      console.error("Error al guardar carrito:", e);
      showToast(tr("errorSaving") || "Error");
      return false;
    }
  };

  // Quita un solo regalo comprado
  const removeFromHistory = async (entry) => {
    const list = userProfile?.committedGifts || [];
    const idx = list.findIndex((g) => giftKey(g) === giftKey(entry));
    if (idx === -1) return;
    const ok = await saveCommittedGifts(list.filter((_, i) => i !== idx));
    if (ok) showToast(lang === "es" ? "Regalo quitado" : lang === "fi" ? "Lahja poistettu" : "Gift removed");
  };

  const savePersonalPrice = async (entry) => {
    const value = editingPriceValue.trim();
    if (value !== "" && (!Number.isFinite(Number(value)) || Number(value) < 0)) {
      showToast(tr("invalidPrice"));
      return;
    }
    const list = userProfile?.committedGifts || [];
    const idx = list.findIndex((gift) => giftKey(gift) === giftKey(entry));
    if (idx === -1) return;

    const updated = list.map((gift, i) => {
      if (i !== idx) return gift;
      const copy = { ...gift };
      if (value === "") delete copy.personalPrice;
      else copy.personalPrice = value;
      return copy;
    });

    if (await saveCommittedGifts(updated)) {
      setEditingPriceEntry(null);
      showToast(tr("personalPriceSaved"));
    }
  };

  // Quita todos los comprados de este grupo
  const clearPurchased = async () => {
    const msg = lang === "es"
      ? "¿Quitar todos los regalos ya comprados de este grupo? Dejarán de contar en tu presupuesto."
      : lang === "fi"
      ? "Poistetaanko kaikki ostetut lahjat tästä ryhmästä? Ne eivät enää näy budjetissasi."
      : "Remove all purchased gifts from this group? They will no longer count toward your budget.";
    if (!window.confirm(msg)) return;
    const list = userProfile?.committedGifts || [];
    const newList = list.filter((g) => !(g.purchased && (!g.familyId || g.familyId === fid)));
    const ok = await saveCommittedGifts(newList);
    if (ok) showToast(lang === "es" ? "Comprados quitados" : lang === "fi" ? "Ostetut poistettu" : "Purchased gifts removed");
  };

  const pendingGifts = (totals.gifts || []).filter((e) => !e.purchased);
  const purchasedGifts = (totals.gifts || []).filter((e) => e.purchased);

  // ── Formato de precio sin conversión ─────────────────────
  const formatPrice = (price, currency) => {
    if (!price) return "";
    const s = currencySymbol(currency || "USD");
    return `${s}${parseFloat(price).toFixed(2)}`;
  };

  // ── Fila de un regalo en el carrito ──────────────────────
  const renderEntry = (entry, key) => (
    <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid " + (darkMode ? "#2e2e2e" : "#f0ece6"), opacity: entry.purchased ? 0.7 : 1 }}>
      {/* Foto */}
      {entry.gift?.photoUrl
        ? <img src={entry.gift.photoUrl} alt="" style={{ width: 44, height: 44, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
        : <div style={{ width: 44, height: 44, borderRadius: 8, background: darkMode ? "#2a2a2a" : "#f0ece6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>🎁</div>
      }

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: darkMode ? "#e8e2d9" : "#1a1a1a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {entry.gift?.name || (lang === "es" ? "Regalo" : lang === "fi" ? "Lahja" : "Gift")}
        </div>
        <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
          {entry.memberName ? entry.memberName + " · " : ""}{entry.purchased ? "✅ " + tr("purchased") : "☝️ " + tr("committed")}
        </div>
        {editingPriceEntry === giftKey(entry) ? (
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
            <input
              aria-label={lang === "es" ? "Precio personal" : lang === "fi" ? "Oma hinta" : "Personal price"}
              className="campo-input"
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={editingPriceValue}
              onChange={(event) => setEditingPriceValue(event.target.value)}
              style={{ width: 100, padding: "4px 6px", fontSize: 12 }}
            />
            <button
              onClick={() => savePersonalPrice(entry)}
              title={tr("save")}
              style={{ fontSize: 11, background: "#27ae60", color: "#fff", border: "none", borderRadius: 6, padding: "4px 7px", cursor: "pointer" }}>
              ✓
            </button>
            <button
              onClick={() => setEditingPriceEntry(null)}
              title={tr("cancel")}
              style={{ fontSize: 11, background: "transparent", color: "#777", border: "1px solid #aaa", borderRadius: 6, padding: "4px 7px", cursor: "pointer" }}>
              ✕
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
            {(entry.gift?.price || entry.price) && (
              <span style={{ fontSize: 13, fontWeight: 600, color: "#d4a853" }}>
                {formatPrice(entry.gift?.price ?? entry.price, entry.gift?.currency || entry.currency)}
              </span>
            )}
            <button
              onClick={() => {
                setEditingPriceEntry(giftKey(entry));
                setEditingPriceValue(String(entry.personalPrice ?? entry.price ?? ""));
              }}
              title={lang === "es" ? "Cambiar solo en mi carrito" : lang === "fi" ? "Muuta vain omassa ostoskorissa" : "Change price in my cart only"}
              style={{ fontSize: 11, background: "transparent", color: "#888", border: "1px solid #aaa", borderRadius: 6, padding: "2px 6px", cursor: "pointer" }}>
              ✎
            </button>
          </div>
        )}
      </div>

      {/* Acciones */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {!entry.purchased ? (
          <>
            <button
              onClick={() => markAsPurchased && markAsPurchased(entry)}
              style={{ fontSize: 11, background: "#27ae60", color: "#fff", border: "none", borderRadius: 8, padding: "4px 8px", cursor: "pointer" }}>
              ✅
            </button>
            <button
              onClick={() => uncommitGift && uncommitGift({ wishlistId: entry.wishlistId, giftId: entry.gift?.id })}
              style={{ fontSize: 11, background: "transparent", color: "#e67e22", border: "1px solid #e67e22", borderRadius: 8, padding: "4px 8px", cursor: "pointer" }}>
              ✕
            </button>
          </>
        ) : (
          <>
            <button
              onClick={() => markAsNotPurchased && markAsNotPurchased(entry)}
              title={tr("markNotPurchased")}
              aria-label={tr("markNotPurchased")}
              style={{ fontSize: 11, background: "transparent", color: "#e67e22", border: "1px solid #e67e22", borderRadius: 8, padding: "4px 8px", cursor: "pointer" }}>
              ↩ {lang === "es" ? "No" : lang === "fi" ? "Ei" : "Undo"}
            </button>
            <button
              onClick={() => removeFromHistory(entry)}
              title={lang === "es" ? "Quitar del historial" : lang === "fi" ? "Poista historiasta" : "Remove from history"}
              style={{ fontSize: 13, background: "transparent", color: "#c0392b", border: "1px solid #c0392b", borderRadius: 8, padding: "4px 8px", cursor: "pointer" }}>
              🗑
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowBudgetScreen(false); }}>
      <div className="modal" style={{ overflowY: "auto", maxHeight: "88vh" }}>

        {/* ── Header ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <h3 style={{ fontFamily: "Playfair Display, serif", fontSize: 18, margin: 0 }}>
            💰 {lang === "es" ? "Mi presupuesto" : lang === "fi" ? "Budjettini" : "My budget"}
          </h3>
          <button style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#aaa" }}
            onClick={() => setShowBudgetScreen(false)}>✕</button>
        </div>

        {/* ── Selector de grupo ── */}
        {userFamilies && userFamilies.length > 1 && (
          <div style={{ marginBottom: 16, position: "relative" }}>
            <button
              onClick={() => setShowBudgetGroupDropdown(!showBudgetGroupDropdown)}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", borderRadius: 12, border: "1px solid " + (darkMode ? "#2e2e2e" : "#e0dbd2"), background: darkMode ? "#1a1a1a" : "#faf8f5", cursor: "pointer", fontSize: 13, color: darkMode ? "#e8e2d9" : "#1a1a1a" }}>
              <span>{groupIcons[fid] || "🌙"} {family?.name}</span>
              <span style={{ color: "#aaa" }}>{showBudgetGroupDropdown ? "▲" : "▼"}</span>
            </button>
            {showBudgetGroupDropdown && (
              <div style={{ position: "absolute", top: "110%", left: 0, right: 0, background: darkMode ? "#1e1e1e" : "#fff", border: "1px solid " + (darkMode ? "#2e2e2e" : "#e0dbd2"), borderRadius: 12, zIndex: 10, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.15)" }}>
                {userFamilies.map((f) => (
                  <div key={f.id}
                    onClick={() => { setCurrentFamily(f); setShowBudgetGroupDropdown(false); }}
                    style={{ padding: "10px 14px", cursor: "pointer", fontSize: 13, color: darkMode ? "#e8e2d9" : "#1a1a1a", background: f.id === fid ? (darkMode ? "#2a2a2a" : "#f5f0e8") : "transparent", borderBottom: "1px solid " + (darkMode ? "#2e2e2e" : "#f0ece6") }}>
                    {groupIcons[f.id] || "🌙"} {f.name}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Barra de presupuesto ── */}
        {totals.budget > 0 && (
          <div style={{ background: darkMode ? "#1a1a1a" : "#faf8f5", border: "1px solid " + (darkMode ? "#2e2e2e" : "#e0dbd2"), borderRadius: 14, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ fontSize: 22, fontWeight: 700, color: pct > 90 ? "#c0392b" : (darkMode ? "#e8e2d9" : "#1a1a1a") }}>
                {sym}{usedAmt.toFixed(2)}
              </span>
              <span style={{ fontSize: 13, color: "#999" }}>/ {sym}{totals.budget.toFixed(2)}</span>
            </div>
            <div style={{ height: 8, background: darkMode ? "#2e2e2e" : "#f0ece6", borderRadius: 4, overflow: "hidden", marginBottom: 8 }}>
              <div style={{ height: "100%", width: pct + "%", background: barColor, borderRadius: 4, transition: "width 0.4s" }} />
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 11, color: "#999" }}>
              <span>✅ {lang === "es" ? "Gastado" : lang === "fi" ? "Käytetty" : "Spent"}: {sym}{totals.spent.toFixed(2)}</span>
              <span>☝️ {lang === "es" ? "Reservado" : lang === "fi" ? "Varattu" : "Reserved"}: {sym}{totals.committed.toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* ── Editar presupuesto ── */}
        <div style={{ marginBottom: 16 }}>
          <label className="campo-label">
            {lang === "es" ? "Mi presupuesto para este grupo" : lang === "fi" ? "Budjettini tälle ryhmälle" : "My budget for this group"}
            {" "}({sym})
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="campo-input"
              style={{ flex: 1 }}
              placeholder="0.00"
              value={budgetInput}
              inputMode="decimal"
              onChange={(e) => setBudgetInput(e.target.value.replace(/[^0-9.]/g, ""))}
            />
            <button
              className="btn btn-dorado"
              onClick={saveBudget}
              disabled={savingBudget}
            >
              {savingBudget ? "..." : tr("save")}
            </button>
          </div>
        </div>

        {/* ── Carrito: regalos por comprar ── */}
        {pendingGifts.length > 0 && (
          <div>
            <p style={{ fontSize: 11, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 10 }}>
              {lang === "es" ? "Por comprar" : lang === "fi" ? "Ostettavat" : "To buy"} ({pendingGifts.length})
            </p>
            {pendingGifts.map((entry, i) => renderEntry(entry, "p" + i))}
          </div>
        )}

        {/* ── Carrito: ya comprados (plegable) ── */}
        {purchasedGifts.length > 0 && (
          <div style={{ marginTop: pendingGifts.length > 0 ? 18 : 0 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <p
                onClick={() => setShowPurchased(!showPurchased)}
                style={{ fontSize: 11, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.8px", margin: 0, cursor: "pointer" }}>
                {showPurchased ? "▼" : "▶"} {lang === "es" ? "Ya comprados" : lang === "fi" ? "Ostetut" : "Purchased"} ({purchasedGifts.length})
              </p>
              {showPurchased && (
                <button
                  onClick={clearPurchased}
                  style={{ fontSize: 11, background: "transparent", color: "#c0392b", border: "1px solid #c0392b", borderRadius: 8, padding: "3px 8px", cursor: "pointer" }}>
                  🗑 {lang === "es" ? "Quitar todos" : lang === "fi" ? "Poista kaikki" : "Remove all"}
                </button>
              )}
            </div>
            {showPurchased && purchasedGifts.map((entry, i) => renderEntry(entry, "c" + i))}
          </div>
        )}

        {(totals.gifts || []).length === 0 && (
          <div style={{ textAlign: "center", padding: "30px 0", color: "#aaa" }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🎁</div>
            <p style={{ fontSize: 13 }}>
              {lang === "es" ? "Aún no has reservado ningún regalo" : lang === "fi" ? "Et ole vielä varannut lahjoja" : "You haven't reserved any gifts yet"}
            </p>
          </div>
        )}

        {/* ── Botón cerrar ── */}
        <button className="btn btn-gris" style={{ width: "100%", marginTop: 16 }} onClick={() => setShowBudgetScreen(false)}>
          {tr("close") || (lang === "es" ? "Cerrar" : "Close")}
        </button>

      </div>
    </div>
  );
}
